import { Link, useLocation } from "react-router-dom";
import ContentPage from "../components/ContentPage";
import {
  FaHome,
  FaSearch,
  FaArrowLeft,
  FaExclamationTriangle,
} from "react-icons/fa";

function NotFound() {
  const location = useLocation();

  const isApiError = location.pathname.startsWith("/api");
  const isUserProfile = location.pathname.startsWith("/users/");
  const isPoemDetail = location.pathname.startsWith("/poems/");

  let errorTitle = "Page Not Found";
  let errorMessage =
    "Oops! The page you're looking for doesn't exist. It might have been moved, deleted, or you entered the wrong URL.";
  let errorIcon = "404";

  if (isApiError) {
    errorTitle = "API Endpoint Not Found";
    errorMessage =
      "The requested API endpoint doesn't exist or is not available.";
    errorIcon = "API";
  } else if (isUserProfile) {
    errorTitle = "User Profile Not Found";
    errorMessage =
      "The user profile you're looking for doesn't exist or the pen name may have changed.";
    errorIcon = "👤";
  } else if (isPoemDetail) {
    errorTitle = "Poem Not Found";
    errorMessage =
      "The poem you're looking for doesn't exist or may have been removed.";
    errorIcon = "📝";
  }

  return (
    <ContentPage title="Page Not Found" narrow>
      <div className="w-full text-center py-4">
        <div className="mb-6">
          {isApiError ? (
            <div className="text-5xl mb-4">
              <FaExclamationTriangle className="text-[#f26522] mx-auto" />
            </div>
          ) : (
            <div
              className="text-7xl sm:text-8xl font-bold text-[#ffd4bc] mb-3 leading-none"
              style={{ fontFamily: "var(--font-peer-serif)" }}
            >
              {errorIcon}
            </div>
          )}
          <div className="w-16 h-1 bg-[#f26522] mx-auto rounded-full" />
        </div>

        <h1 className="content-page__title text-center mb-3">{errorTitle}</h1>
        <p className="content-page__subtitle text-center max-w-md mx-auto">
          {errorMessage}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
          <Link to="/" className="peer-home-btn-primary px-6 py-3">
            <FaHome className="mr-2" aria-hidden="true" />
            Go Home
          </Link>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="peer-home-btn-secondary px-6 py-3"
          >
            <FaArrowLeft className="mr-2" aria-hidden="true" />
            Go Back
          </button>
        </div>

        <div className="p-6 peer-home-page-card text-left">
          <h3 className="mb-1">Need help?</h3>
          <p className="text-[#777777] mb-4 text-sm">Try these popular pages:</p>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/poems" className="peer-home-link text-sm">
              Browse Poems
            </Link>
            <Link to="/about" className="peer-home-link text-sm">
              About Us
            </Link>
            <Link to="/feedback" className="peer-home-link text-sm">
              Feedback
            </Link>
            <Link to="/writers" className="peer-home-link text-sm">
              Writers
            </Link>
          </div>

          {isUserProfile && (
            <div className="mt-4 pt-4 border-t border-[#eeeeee]">
              <p className="text-[#777777] text-sm mb-2">
                Looking for a specific user?
              </p>
              <Link
                to="/writers"
                className="peer-home-link text-sm inline-flex items-center gap-1"
              >
                <FaSearch aria-hidden="true" />
                Browse writers
              </Link>
            </div>
          )}

          {isPoemDetail && (
            <div className="mt-4 pt-4 border-t border-[#eeeeee]">
              <p className="text-[#777777] text-sm mb-2">
                Looking for a specific poem?
              </p>
              <Link
                to="/"
                className="peer-home-link text-sm inline-flex items-center gap-1"
              >
                <FaSearch aria-hidden="true" />
                Browse poems
              </Link>
            </div>
          )}
        </div>

        {process.env.NODE_ENV === "development" && (
          <div className="mt-6 p-4 bg-[#fff5ef] rounded-xl border border-[#ffd4bc] text-left">
            <h4 className="text-sm font-semibold text-[#333333] mb-2">
              Debug Info
            </h4>
            <p className="text-xs text-[#777777] mb-1">
              <strong>URL:</strong> {location.pathname}
            </p>
            <p className="text-xs text-[#777777] mb-1">
              <strong>Timestamp:</strong> {new Date().toLocaleString()}
            </p>
            <p className="text-xs text-[#777777]">
              <strong>Error Type:</strong>{" "}
              {isApiError
                ? "API"
                : isUserProfile
                ? "User Profile"
                : isPoemDetail
                ? "Poem"
                : "Page"}
            </p>
          </div>
        )}
      </div>
    </ContentPage>
  );
}

export default NotFound;
